/**
 * Registry federado de componentes de missão.
 *
 * Substitui o lookup hardcoded `ATLAS_COMPONENTS[missionId]` em MissionSlide.
 * Cada namespace (atlas, lab.form, lab.collab, lab.assess, ...) registra um
 * mapa próprio; o resolve() acha o registro certo escolhendo o namespace
 * mais específico que casa com o missionId.
 *
 * Ex.: missionId "lab.form.openQuestion" tenta resolver em ordem:
 *   "lab.form"  → bate, se LAB_FORM_COMPONENTS tem a chave
 *   "lab"       → fallback (vazio em v1)
 *
 * Para registrar um namespace novo, edite NAMESPACE_MAP abaixo.
 */

import type { ComponentType } from 'react'

import { ATLAS_COMPONENTS } from '../games/atlas/components'
import type { MissionComponentProps } from '../games/atlas/components'
import {
  LAB_ASSESS_COMPONENTS,
  LAB_COLLAB_COMPONENTS,
  LAB_FORM_COMPONENTS,
} from '../components/lab'

export interface MissionRegistration {
  /** Componente renderizado na tela do aluno e do mestre. */
  component: ComponentType<MissionComponentProps>
  /**
   * Painel opcional do mestre — renderizado como dock lateral pelo
   * GameMasterPanel quando presente. Recebe as mesmas props.
   */
  masterPanel?: ComponentType<MissionComponentProps>
}

type NamespaceMap = Record<string, Record<string, MissionRegistration>>

const NAMESPACE_MAP: NamespaceMap = {
  'atlas': ATLAS_COMPONENTS,
  'lab.form': LAB_FORM_COMPONENTS,
  'lab.assess': LAB_ASSESS_COMPONENTS,
  'lab.collab': LAB_COLLAB_COMPONENTS,
}

export const COMPONENT_REGISTRY = {
  /**
   * Resolve missionId → registro. Tenta namespaces do mais longo pro mais
   * curto, permitindo que "lab.form.openQuestion" seja achado em "lab.form"
   * e que futuros "lab.collab.postits" sejam achados em "lab.collab".
   */
  resolve(missionId: string): MissionRegistration | null {
    const parts = missionId.split('.')
    for (let i = parts.length - 1; i > 0; i--) {
      const ns = parts.slice(0, i).join('.')
      const map = NAMESPACE_MAP[ns]
      if (map && map[missionId]) return map[missionId]
    }
    return null
  },
}
